import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import MIcon from "../icons/MIcon";
import SpinnerIcon from "../icons/SpinnerIcon";
import Toggle from "../components/ui/Toggle";
import MessageBanner from "../components/ui/MessageBanner";
import { useSettings } from "../SettingsContext";
import {
  AVAILABLE_MODELS,
  FEATURE_FLAG_PAGES,
} from "../constants/settingsConfig";
import { getUserErrorMessage } from "../utils/errors";

const Settings = () => {
  const { t } = useTranslation();
  const {
    selectedModel,
    featureFlags,
    loading,
    error,
    refresh,
    setSelectedModel,
    setFeatureFlag,
    resetSettings,
  } = useSettings();

  const [message, setMessage] = useState({ text: "", type: "" });
  const [savingKey, setSavingKey] = useState(null); // "model" | feature key | "reset" | null

  const handleModelChange = async (e) => {
    const next = e.target.value;
    setSavingKey("model");
    setMessage({ text: "", type: "" });
    try {
      await setSelectedModel(next);
      setMessage({ text: t("settings.msg.modelUpdated"), type: "success" });
    } catch (err) {
      setMessage({ text: getUserErrorMessage(err, "save"), type: "error" });
    } finally {
      setSavingKey(null);
    }
  };

  const handleFlagChange = async (key, enabled) => {
    setSavingKey(key);
    setMessage({ text: "", type: "" });
    try {
      await setFeatureFlag(key, enabled);
      setMessage({
        text: enabled
          ? t("settings.msg.pageVisible")
          : t("settings.msg.pageHidden"),
        type: "success",
      });
    } catch (err) {
      setMessage({ text: getUserErrorMessage(err, "save"), type: "error" });
    } finally {
      setSavingKey(null);
    }
  };

  const handleReset = async () => {
    setSavingKey("reset");
    setMessage({ text: "", type: "" });
    try {
      await resetSettings();
      setMessage({ text: t("settings.msg.settingsReset"), type: "success" });
    } catch (err) {
      setMessage({ text: getUserErrorMessage(err, "save"), type: "error" });
    } finally {
      setSavingKey(null);
    }
  };

  // Show a loading state on the very first hydration. Subsequent refreshes
  // keep the existing UI rendered (optimistic updates already populate state).
  const isInitialLoading = loading && !savingKey;

  return (
    <div className="min-h-screen px-4 sm:px-8 py-10">
      <div className="max-w-5xl mx-auto space-y-8">
        {/* ── Page Header ── */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-brand dark:bg-brand flex items-center justify-center shadow-brand-glow">
              <MIcon name="settings" className="text-white !text-[24px]" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-semibold text-on-surface-light dark:text-on-surface tracking-tight">
                {t("nav.settings")}
              </h1>
              <p className="text-sm text-on-surface-light-variant dark:text-on-surface-variant">
                {t("settings.subtitle")}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleReset}
            disabled={savingKey !== null || isInitialLoading}
            className="flex items-center gap-1.5 text-xs text-on-surface-light-variant dark:text-on-surface-variant hover:text-on-surface-light dark:hover:text-on-surface transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {savingKey === "reset" ? (
              <SpinnerIcon />
            ) : (
              <MIcon name="restart_alt" className="sm" />
            )}
            <span>{t("settings.resetDefaults")}</span>
          </button>
        </div>

        {/* ── Load error banner (initial fetch failed) ── */}
        {error && !isInitialLoading && (
          <div className="rounded-lg px-4 py-3 text-sm flex items-start gap-2.5 bg-red-500/10 dark:bg-red-400/10 border border-red-500/20 dark:border-red-400/20 text-red-600 dark:text-red-400">
            <span className="mt-0.5 shrink-0">
              <MIcon name="error" className="sm" />
            </span>
            <span className="flex-1">{getUserErrorMessage(error, "load")}</span>
            <button
              type="button"
              onClick={refresh}
              className="text-xs font-medium underline hover:no-underline"
            >
              {t("settings.retry")}
            </button>
          </div>
        )}

        {isInitialLoading ? (
          <div className="flat-card rounded-xl animate-float-in">
            <div className="p-12 flex items-center justify-center text-on-surface-light-variant dark:text-on-surface-variant">
              <SpinnerIcon />
              <span className="ml-2 text-sm">{t("settings.loading")}</span>
            </div>
          </div>
        ) : (
          <>
            {/* ── Active Model Card ── */}
            <div className="flat-card rounded-xl animate-float-in">
              <div className="p-6 sm:p-8">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-brand/10 dark:bg-brand/15 flex items-center justify-center text-brand dark:text-brand shrink-0">
                    <MIcon name="auto_awesome" />
                  </div>
                  <div className="flex-1">
                    <h2 className="font-display text-base font-semibold text-on-surface-light dark:text-on-surface">
                      {t("settings.activeModel")}
                    </h2>
                    <p className="text-xs text-on-surface-light-variant dark:text-on-surface-variant">
                      {t("settings.activeModelDesc")}
                    </p>
                  </div>
                  {savingKey === "model" && (
                    <span className="text-on-surface-light-variant dark:text-on-surface-variant">
                      <SpinnerIcon />
                    </span>
                  )}
                </div>

                <div className="relative max-w-md">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-on-surface-light-variant dark:text-on-surface-variant pointer-events-none">
                    <MIcon name="memory" className="sm" />
                  </div>
                  <select
                    value={selectedModel}
                    onChange={handleModelChange}
                    disabled={savingKey !== null}
                    className="auth-input w-full pl-11 pr-10 py-3 rounded-lg text-sm font-body appearance-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {AVAILABLE_MODELS.map((model) => (
                      <option key={model.value} value={model.value}>
                        {model.label}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3.5 top-1/2 -translate-y-1/2 text-on-surface-light-variant dark:text-on-surface-variant pointer-events-none">
                    <MIcon name="expand_more" className="sm" />
                  </div>
                </div>

                <p className="mt-3 text-xs text-on-surface-light-variant dark:text-on-surface-variant">
                  {t("settings.currentlySelected")}{" "}
                  <span className="font-mono text-on-surface-light dark:text-on-surface">
                    {selectedModel}
                  </span>
                </p>
              </div>
            </div>

            {/* ── Feature Flags Card ── */}
            <div className="flat-card rounded-xl">
              <div className="p-6 sm:p-8">
                <div className="flex items-start gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-brand/10 dark:bg-brand/15 flex items-center justify-center text-brand dark:text-brand shrink-0">
                    <MIcon name="flag" />
                  </div>
                  <div>
                    <h2 className="font-display text-base font-semibold text-on-surface-light dark:text-on-surface">
                      {t("settings.pageVisibility")}
                    </h2>
                    <p className="text-xs text-on-surface-light-variant dark:text-on-surface-variant">
                      {t("settings.pageVisibilityDesc")}
                    </p>
                  </div>
                </div>

                <ul className="divide-y divide-surface-light-highest/60 dark:divide-white/5">
                  {FEATURE_FLAG_PAGES.map((page) => {
                    const enabled = featureFlags[page.key] ?? true;
                    const isSavingThis = savingKey === page.key;
                    return (
                      <li
                        key={page.key}
                        className="flex items-center gap-4 py-4 first:pt-0 last:pb-0"
                      >
                        <div className="w-10 h-10 rounded-lg bg-surface-light-low dark:bg-surface-container-low border border-surface-light-highest dark:border-white/5 flex items-center justify-center text-on-surface-light-variant dark:text-on-surface-variant shrink-0">
                          <MIcon name={page.icon} />
                        </div>

                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-on-surface-light dark:text-on-surface">
                            {t(`nav.${page.key}`)}
                          </p>
                          <p className="text-xs text-on-surface-light-variant dark:text-on-surface-variant">
                            {t(`settings.pageDesc.${page.key}`)}
                          </p>
                          <p className="text-[11px] font-mono text-on-surface-light-variant/70 dark:text-on-surface-variant/70 mt-0.5">
                            {page.path}
                          </p>
                        </div>

                        <div className="flex items-center gap-3 shrink-0">
                          {isSavingThis ? (
                            <span className="text-on-surface-light-variant dark:text-on-surface-variant">
                              <SpinnerIcon />
                            </span>
                          ) : (
                            <span
                              className={`text-xs font-medium ${
                                enabled
                                  ? "text-emerald-600 dark:text-emerald-400"
                                  : "text-on-surface-light-variant dark:text-on-surface-variant"
                              }`}
                            >
                              {enabled ? t("settings.visible") : t("settings.hidden")}
                            </span>
                          )}
                          <div
                            className={
                              savingKey !== null && !isSavingThis
                                ? "opacity-60 pointer-events-none"
                                : ""
                            }
                          >
                            <Toggle
                              enabled={enabled}
                              onChange={(next) =>
                                handleFlagChange(page.key, next)
                              }
                              label={t("settings.togglePage", {
                                page: t(`nav.${page.key}`),
                              })}
                            />
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>

                <MessageBanner message={message} />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Settings;
